import { TriangleAlert } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './button';

/**
 * Hỏi trước khi đóng một form **còn thay đổi chưa lưu**.
 *
 * Người dùng yêu cầu 2026-10-01: đang soạn việc mà bấm ra ngoài popup (hay bấm
 * X, Esc, Huỷ) thì cả form biến mất cùng mọi thứ vừa gõ — một cú bấm trượt mép
 * hộp là mất mô tả dài cả đoạn.
 *
 * Hộp hỏi ở **giữa màn hình**, không dùng `useConfirmPopover`: popover neo theo
 * nút vừa bấm, mà một nửa số lần đóng là bấm vào lớp phủ hoặc nhấn Esc — không
 * có nút nào để neo.
 *
 * Không có gì đổi thì đóng ngay, không hỏi: hỏi cả khi chẳng có gì để mất là
 * dạy người dùng bấm "Bỏ" theo phản xạ, và tới lúc có thứ để mất thì họ bấm
 * luôn.
 *
 * ```tsx
 * const discard = useDiscardConfirm(dirty);
 * const requestClose = () => discard.guard(onClose);
 * <div onClick={requestClose} /> … {discard.dialog}
 * ```
 */
export function useDiscardConfirm(dirty: boolean): {
  readonly guard: (close: () => void) => void;
  readonly dialog: ReactNode;
} {
  const [pending, setPending] = useState<(() => void) | null>(null);
  // Đọc qua ref: chỗ gọi thường đặt `guard` vào listener Esc dựng một lần.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  /*
    Đóng tab / tải lại trang cũng là "thoát". Trình duyệt chỉ cho hiện hộp
    chuẩn của nó (không đổi được chữ), nhưng vẫn hơn mất trắng.
  */
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const guard = useCallback((close: () => void): void => {
    if (dirtyRef.current) setPending(() => close);
    else close();
  }, []);

  const dialog = pending ? (
    <DiscardChangesDialog
      onDiscard={() => {
        setPending(null);
        pending();
      }}
      onKeep={() => setPending(null)}
    />
  ) : null;

  return { guard, dialog };
}

function DiscardChangesDialog({
  onDiscard,
  onKeep,
}: {
  readonly onDiscard: () => void;
  readonly onKeep: () => void;
}): ReactNode {
  const { t } = useTranslation();

  /*
    Esc = ở lại soạn tiếp. Nghe ở pha **capture** và chặn lan truyền: form bên
    dưới cũng nghe Esc để đóng, và Esc lúc đang hỏi mà lọt xuống thì nó hỏi lại
    chính câu này.
  */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onKeep();
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [onKeep]);

  return createPortal(
    // `z-[60]`: cao hơn `z-50` của form đang mở bên dưới.
    <div className="relative z-[60]">
      <div className="fixed inset-0 popup-scrim" onClick={onKeep} aria-hidden />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="discard-changes-title"
        aria-describedby="discard-changes-body"
        className="fixed left-1/2 top-1/2 flex w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 flex-col gap-3 rounded-DEFAULT border border-border bg-background p-4 shadow-xl glass-popup-sm on-scrim"
      >
        <div className="flex items-start gap-2">
          <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-rag-amber" />
          <h2 id="discard-changes-title" className="text-base font-semibold">
            {t('common.discardChanges.title')}
          </h2>
        </div>
        <p id="discard-changes-body" className="text-sm">
          {t('common.discardChanges.body')}
        </p>
        <div className="flex justify-end gap-2">
          {/* `autoFocus` vào "Tiếp tục soạn": Enter theo phản xạ không được làm mất chữ. */}
          <Button type="button" variant="outline" onClick={onKeep} autoFocus>
            {t('common.discardChanges.keep')}
          </Button>
          <Button type="button" variant="destructive" onClick={onDiscard}>
            {t('common.discardChanges.discard')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
